import { Contract, Order } from "@/types";
import { useTranslation } from "react-i18next";
import { Grid } from "@mantine/core";
import Text from "@/components/atoms/Text";
import Accordion from "@/components/organisms/AccordionDrawer";
import { zodResolver } from "@hookform/resolvers/zod";
import { postFinalAmount } from "@/services/Orders";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import styles from "./styles.module.css";
import { useAuth } from "@/contexts/AuthContext";
import { useGetContractById } from "@/services/Contracts";
import TextInput from "@/components/atoms/TextInput";
import CTA from "@/components/atoms/CTA";
import { useGetOpenTasks } from "@/services/Tasks";
import {
  FinalAmountFormInput,
  FinalAmountValidationSchema,
} from "@/components/pages/Tasks/validation/finalAmountValidation";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { notifications } from "@mantine/notifications";

interface FinalAmountAccordionProps {
  order: Order;
}

const FinalAmountAccordion = ({ order }: FinalAmountAccordionProps) => {
  const { t } = useTranslation();

  const { currentCompany } = useAuth();
  const queryClient = useQueryClient();
  const { data: contract } = useGetContractById(order.contractId as number, {
    enabled: !!order.contractId,
  });

  const { data: tasks } = useGetOpenTasks(currentCompany?.companyId as number, "FINAL_AMOUNT", {
    enabled: !!currentCompany?.companyId,
  });
  const {
    handleSubmit,
    control,
    formState: { isValid },
    setError,
  } = useForm<FinalAmountFormInput>({
    resolver: zodResolver(FinalAmountValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: {
      amount: order?.amount || 0,
    },
  });

  const { mutate: mutateSingleFinalAmount, isPending } = useMutation({
    mutationFn: async ({ orderId, amount, taskId }: { orderId: number; amount: number; taskId: number }) => {
      await postFinalAmount(orderId, amount, taskId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getOpenTasks", currentCompany?.companyId],
      });
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("notifications:orders.finalAmountSet"))
      );
    },
    onError: () => {
      notifications.show(
        ErrorNotificationObj(t("notifications:generic.error"), t("notifications:orders.finalAmountSetError"))
      );
      setError("amount", {
        type: "manual",
        message: t("notifications:orders.finalAmountSetError"),
      });
    },
  });

  const onSubmit = async (data: FinalAmountFormInput) => {
    if (!order) return;
    const amount = data.amount;
    const originalAmount = order.amount;
    const amountDeviation = order.amountDeviation || 0;

    const deviation: number = (originalAmount / 100) * amountDeviation;
    const lowEnd: number = originalAmount - deviation;
    const highEnd: number = originalAmount + deviation;
    const taskId = tasks?.find((task) => task.connected === order.id)?.id;

    if (amount > highEnd || amount < lowEnd) {
      setError(`amount`, {
        type: "manual",
        message: t("tasks:legalFinalAmount") + lowEnd + order.priceUnit?.unit + " - " + highEnd + order.priceUnit?.unit,
      });
      return;
    }

    await mutateSingleFinalAmount({
      orderId: order.id,
      amount: amount,
      taskId: taskId as number,
    });
  };

  const findWeightResponsible = (contract: Contract) => {
    switch (contract.weightResponsible.toUpperCase()) {
      case "SELLER":
        return contract.contractSellers[0];
      case "BUYER":
        return contract.contractBuyers[0];
      default:
        return null;
    }
  };

  const weightResponsible = contract ? findWeightResponsible(contract) : null;

  const hasFinalAmountTask = tasks?.some((task) => task.connected === order.id);

  return (
    <Accordion title={t("orders:finalAmount")}>
      <form id="final-amount-form" onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Grid className={styles.grid}>
          <Grid.Col span={5}>
            <Text size="sm" color={"secondary"}>
              {t("contracts:weightResponsible")}
            </Text>
          </Grid.Col>

          <Grid.Col span={7}>
            <Text size="sm" color={"primary"}>
              {weightResponsible && weightResponsible.companyName}
            </Text>
          </Grid.Col>

          {/*
          <Grid.Col span={5}>
            <Text size="sm" color={"secondary"}>
              {t("orders:finalAmount")}
            </Text>
          </Grid.Col>
          <Grid.Col span={7}>
            <Text size="sm" color={"primary"}>
              {order.finalAmount && order.finalAmount + " " + order.unit?.unit}
            </Text>
          </Grid.Col>
          */}

          <Grid.Col span={10}>
            <Text size="sm" color={"secondary"}>
              {t("orders:finalAmountInfo")}
            </Text>
          </Grid.Col>

          {weightResponsible?.id === currentCompany?.companyId && !hasFinalAmountTask && (
            <Grid.Col span={12}>
              <Text size="sm" color={"primary"}>
                {t("orders:finalAmountHasBeenSet")}
              </Text>
            </Grid.Col>
          )}
          {weightResponsible?.id !== currentCompany?.companyId &&
            order.status !== "AWAITING_FINAL_AMOUNT" &&
            order.status !== "REQUIRES_ACTION" && (
              <Grid.Col span={12}>
                <Text size="sm" color={"primary"}>
                  {t("orders:finalAmountHasBeenSet")}
                </Text>
              </Grid.Col>
            )}
          {weightResponsible?.id !== currentCompany?.companyId &&
            (order.status === "AWAITING_FINAL_AMOUNT" || order.status === "REQUIRES_ACTION") && (
              <Grid.Col span={12}>
                <Text size="sm" color={"warning"}>
                  {t("orders:weightResponsibleNeedsToSetFinalAmount")}
                </Text>
              </Grid.Col>
            )}
          {weightResponsible?.id === currentCompany?.companyId && hasFinalAmountTask && (
            <>
              <Grid.Col span={6}>
                <Controller
                  control={control}
                  name={"amount"}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t("common:amount") + " (" + order?.unit?.unit + ")"}
                      value={field.value === undefined ? 0 : field.value}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "" || value === undefined) {
                          field.onChange(0);
                          return;
                        }
                        const numValue = parseFloat(value);
                        field.onChange(isNaN(numValue) ? 0 : numValue);
                      }}
                      onBlur={field.onBlur}
                      error={
                        fieldState.error
                          ? fieldState.error.type === "manual"
                            ? fieldState.error.message
                            : t(fieldState.error.message as string)
                          : undefined
                      }
                    />
                  )}
                />
                <CTA type="submit" intent="secondary" mt={10} disabled={!isValid || isPending}>
                  {t("actions:setFinalAmount")}
                </CTA>
              </Grid.Col>
            </>
          )}
        </Grid>
      </form>
    </Accordion>
  );
};

export default FinalAmountAccordion;
